import { Controller } from "@hotwired/stimulus"
import ApexCharts from "apexcharts"

export default class extends Controller {
  connect() {
    this.fetchDataAndRender()
  }

  disconnect() {
    if (this.chart) {
      this.chart.destroy()
    }
  }

  async fetchDataAndRender() {
    try {
      // Get current URL parameters
      const urlParams = new URLSearchParams(window.location.search)

      // Build the fetch URL with the same parameters
      const fetchUrl = `/trends/streams_by_day?${urlParams.toString()}`

      const response = await fetch(fetchUrl)
      const data = await response.json()

      this.renderChart(data)
    } catch (error) {
      console.error('Error fetching chart data:', error)
      this.showError()
    }
  }

  renderChart(data) {
    if (!data || !data.series || data.series.length === 0) {
      this.showEmptyState()
      return
    }

    // Clear the loading message and any existing content
    this.element.innerHTML = ''

    const options = {
      series: data.series,
      chart: {
        type: 'line',
        height: 400,
        toolbar: {
          show: false
        },
        zoom: {
          enabled: false
        }
      },
      stroke: {
        curve: 'smooth',
        width: 2
      },
      xaxis: {
        type: 'datetime',
        labels: {
          format: 'MMM dd'
        }
      },
      yaxis: {
        labels: {
          formatter: function(value) {
            return Math.floor(value).toLocaleString()
          }
        }
      },
      tooltip: {
        shared: true,
        intersect: false,
        custom: function({series, seriesIndex, dataPointIndex, w}) {
          // Get the date string directly from the data to avoid timezone issues
          const dateTimestamp = w.globals.seriesX[seriesIndex][dataPointIndex]
          const date = new Date(dateTimestamp)

          // Format date using UTC to avoid timezone offset issues
          const formattedDate = date.toLocaleDateString('en-US', {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            timeZone: 'UTC'
          })

          let tooltipHtml = '<div class="px-3 py-2" style="min-width: 200px;">'
          tooltipHtml += `<div class="font-semibold text-gray-900 mb-2">${formattedDate}</div>`

          // Build rows for each DSP
          let dspRows = []
          w.globals.seriesNames.forEach((name, index) => {
            const value = series[index][dataPointIndex]
            if (value !== undefined && value !== null) {
              const color = w.globals.colors[index]
              dspRows.push({
                name: name,
                value: value,
                color: color
              })
            }
          })

          // Sort by value descending
          dspRows.sort((a, b) => b.value - a.value)

          // Add each DSP row
          dspRows.forEach(row => {
            tooltipHtml += '<div class="flex items-center justify-between gap-4 py-1">'
            tooltipHtml += `<div class="flex items-center gap-2">`
            tooltipHtml += `<div class="w-3 h-3 rounded-sm" style="background-color: ${row.color};"></div>`
            tooltipHtml += `<span class="text-sm text-gray-700">${row.name}</span>`
            tooltipHtml += `</div>`
            tooltipHtml += `<span class="text-sm font-medium text-gray-900">${row.value.toLocaleString()}</span>`
            tooltipHtml += '</div>'
          })

          // Calculate and add total streams
          const totalStreams = dspRows.reduce((sum, row) => sum + row.value, 0)
          tooltipHtml += '<div class="border-t border-gray-200 mt-2 pt-2">'
          tooltipHtml += '<div class="flex items-center justify-between gap-4">'
          tooltipHtml += `<span class="text-sm font-semibold text-gray-900">Total Streams</span>`
          tooltipHtml += `<span class="text-sm font-semibold text-gray-900">${totalStreams.toLocaleString()}</span>`
          tooltipHtml += '</div>'
          tooltipHtml += '</div>'

          tooltipHtml += '</div>'
          return tooltipHtml
        }
      },
      legend: {
        position: 'bottom',
        horizontalAlign: 'center',
        offsetY: 10
      },
      colors: data.colors || ['#19B26C', '#FF0000', '#FB5069', '#25D0D8', '#FF9800', '#9C27B0'],
      grid: {
        borderColor: '#434343',
        row: {
          colors: ['transparent', 'transparent'],
          opacity: 0.5
        }
      }
    }

    this.chart = new ApexCharts(this.element, options)
    this.chart.render()
  }

  showEmptyState() {
    this.element.innerHTML = '<div class="text-center py-8 text-gray-500">No streaming data available for the selected filters.</div>'
  }

  showError() {
    this.element.innerHTML = '<div class="text-center py-8 text-red-500">Error loading chart data. Please try again.</div>'
  }
}
